import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { Add, Bolt, Close, DragIndicator, Lightbulb, MyLocation, Nfc, Save, Speed, Straighten, SwapHoriz, Thermostat, ToggleOn, WaterDrop } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { bcp47, useI18n } from '../i18n';
import { dashboardApi, signalsApi } from '../api';
import { DashboardWidget, WidgetCatalogItem, DashboardTopicValue } from '../types';
import { reorderWidgets } from '../dashboard/reorderWidgets';
import { SignalsDefinition } from '../signals/types';
import {
  bindStacklightFromCatalog,
  resolveStacklightLive,
  stacklightCommandTarget,
  stacklightWatchTopics,
} from '../signals/stacklightBinding';
import SignalsPreview from '../signals/components/SignalsPreview';
import SignalsCommandDialog from '../signals/components/SignalsCommandDialog';
import SignalsIcon from '../icons/SignalsIcon';
import { useLiveMqttValues } from '../hooks/useLiveMqttValues';
import { SETUP_FLOW_PATH } from '../nav/paths';
import Switch from '@mui/material/Switch';

function formatAge(updatedAt: number | undefined, t: (k: string, v?: Record<string, string | number>) => string): string {
  if (!updatedAt) {
    return t('dashboard.noData');
  }
  const sec = Math.max(0, Math.floor((Date.now() - updatedAt) / 1000));
  if (sec < 5) return t('dashboard.justNow');
  if (sec < 60) return t('dashboard.secondsAgo', { n: sec });
  const min = Math.floor(sec / 60);
  if (min < 60) return t('dashboard.minutesAgo', { n: min });
  return t('dashboard.hoursAgo', { n: Math.floor(min / 60) });
}

function readScalar(value: DashboardTopicValue | null | undefined): number | null {
  if (!value) return null;
  if (typeof value.value === 'number' && Number.isFinite(value.value)) return value.value;
  if (typeof value.temp === 'number' && Number.isFinite(value.temp)) return value.temp;
  if (typeof value.rh === 'number' && Number.isFinite(value.rh)) return value.rh;
  if (value.raw) {
    const n = Number(String(value.raw).trim());
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function readHumidity(value: DashboardTopicValue | null | undefined): number | null {
  if (!value) return null;
  if (typeof value.rh === 'number' && Number.isFinite(value.rh)) return value.rh;
  return readScalar(value);
}

function readText(value: DashboardTopicValue | null | undefined): string {
  if (!value) {
    return '';
  }
  const raw = typeof value.raw === 'string' ? value.raw.trim() : '';
  if (raw && !raw.startsWith('{') && !raw.startsWith('[')) {
    return raw;
  }
  if (value.value != null) {
    return String(value.value);
  }
  return '';
}

function formatQty(n: number, fractionDigits: number): string {
  return n.toLocaleString(bcp47(), {
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  });
}

const DASHBOARD_DND = 'application/x-dashboard-widget';

const DASHBOARD_WIDGET_TYPES = new Set([
  'temperature',
  'modbus',
  'gps_map',
  'sml_meter',
  'nfc_tag',
  'distance',
  'stacklight',
  'switch',
  'relay',
]);

function isKnownDashboardWidget(widget: DashboardWidget): boolean {
  return DASHBOARD_WIDGET_TYPES.has(widget.type);
}

type DashboardDndValue = {
  enabled: boolean;
  draggingId: string | null;
  setDraggingId: (id: string | null) => void;
  setOverId: (id: string | null) => void;
};

const DashboardDndContext = React.createContext<DashboardDndValue>({
  enabled: false,
  draggingId: null,
  setDraggingId: () => {},
  setOverId: () => {},
});

function useDashboardDnd() {
  return React.useContext(DashboardDndContext);
}

function isDashboardWidgetDrag(event: React.DragEvent) {
  return Array.from(event.dataTransfer.types).includes(DASHBOARD_DND);
}

function WidgetDragHandle({ widgetId, tone = 'default' }: { widgetId: string; tone?: 'default' | 'onDark' }) {
  const { t } = useI18n();
  const { enabled, setDraggingId, setOverId } = useDashboardDnd();
  if (!enabled) {
    return null;
  }
  return (
    <Box
      component="span"
      draggable
      role="button"
      tabIndex={0}
      aria-label={t('dashboard.reorder')}
      title={t('dashboard.reorder')}
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData(DASHBOARD_DND, widgetId);
        event.dataTransfer.setData('text/plain', widgetId);
        setDraggingId(widgetId);
      }}
      onDragEnd={() => {
        setDraggingId(null);
        setOverId(null);
      }}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        cursor: 'grab',
        color: tone === 'onDark' ? 'inherit' : 'action.active',
        flexShrink: 0,
        userSelect: 'none',
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <DragIndicator fontSize="small" />
    </Box>
  );
}

const DashboardWidgetSlot: React.FC<{
  widgetId: string;
  draggingId: string | null;
  overId: string | null;
  setOverId: (id: string | null) => void;
  onDropOn: (fromId: string, toId: string) => void;
  children: React.ReactNode;
}> = ({ widgetId, draggingId, overId, setOverId, onDropOn, children }) => (
  <Box
    onDragOver={(event) => {
      if (!isDashboardWidgetDrag(event) && !draggingId) {
        return;
      }
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      if (overId !== widgetId) {
        setOverId(widgetId);
      }
    }}
    onDragLeave={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node)) {
        setOverId(null);
      }
    }}
    onDrop={(event) => {
      event.preventDefault();
      const fromId = event.dataTransfer.getData(DASHBOARD_DND) || event.dataTransfer.getData('text/plain');
      setOverId(null);
      if (fromId) {
        onDropOn(fromId, widgetId);
      }
    }}
    sx={{
      height: '100%',
      opacity: draggingId === widgetId ? 0.5 : 1,
      outline: overId === widgetId && draggingId && draggingId !== widgetId ? '2px solid' : 'none',
      outlineColor: 'primary.main',
      outlineOffset: 2,
      borderRadius: 1,
      transition: 'opacity 120ms',
    }}
  >
    {children}
  </Box>
);

const ClimateWidget: React.FC<{
  widget: DashboardWidget;
  tempValue: DashboardTopicValue | null | undefined;
  rhValue?: DashboardTopicValue | null | undefined;
  onRemove: () => void;
  variant?: 'sensor' | 'modbus';
}> = ({ widget, tempValue, rhValue, onRemove, variant = 'sensor' }) => {
  const { t } = useI18n();
  const temp = readScalar(tempValue);
  const rh = rhValue != null
    ? readHumidity(rhValue)
    : (typeof tempValue?.rh === 'number' && Number.isFinite(tempValue.rh) ? tempValue.rh : null);
  const title = widget.title || widget.topic;
  const updatedAt = Math.max(tempValue?.updatedAt ?? 0, rhValue?.updatedAt ?? 0) || undefined;

  return (
    <Card variant="outlined" sx={{ height: '100%', bgcolor: 'background.paper' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
          <Box display="flex" alignItems="center" gap={0.75} minWidth={0} flex={1}>
            <WidgetDragHandle widgetId={widget.id} />
            <Box minWidth={0} flex={1}>
              <Typography variant="subtitle2" noWrap title={title}>
                {title}
              </Typography>
              {widget.topic ? (
                <Typography variant="caption" color="text.secondary" noWrap display="block" title={widget.topic}>
                  {widget.topic}
                </Typography>
              ) : null}
            </Box>
          </Box>
          <IconButton size="small" aria-label={t('dashboard.remove')} onClick={onRemove}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Box display="flex" gap={2} mt={2} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={1} minWidth={120}>
            <Thermostat sx={{ color: temp != null ? 'error.main' : 'action.disabled', fontSize: 32 }} />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('dashboard.temperature')}
              </Typography>
              <Typography variant="h5" fontWeight={700} color={temp != null ? 'text.primary' : 'text.secondary'}>
                {temp != null ? `${temp.toFixed(1)} \u00b0C` : t('common.emdash')}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1} minWidth={120}>
            <WaterDrop sx={{ color: rh != null ? 'info.main' : 'action.disabled', fontSize: 32 }} />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('dashboard.humidity')}
              </Typography>
              <Typography variant="h5" fontWeight={700} color={rh != null ? 'text.primary' : 'text.secondary'}>
                {rh != null ? `${rh.toFixed(1)} %` : t('common.emdash')}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          {formatAge(updatedAt, t)}
        </Typography>
      </CardContent>
    </Card>
  );
};

const SmlMeterWidget: React.FC<{
  widget: DashboardWidget;
  values: Record<string, DashboardTopicValue | null | undefined>;
  onRemove: () => void;
}> = ({ widget, values, onRemove }) => {
  const { t } = useI18n();
  const energy = readScalar(values[widget.topic]);
  const power = widget.topicPower ? readScalar(values[widget.topicPower]) : null;
  const reverse = widget.topicReverse ? readScalar(values[widget.topicReverse]) : null;
  const title = widget.title || widget.topic;
  const updatedAt = Math.max(
    values[widget.topic]?.updatedAt ?? 0,
    values[widget.topicPower || '']?.updatedAt ?? 0,
    values[widget.topicReverse || '']?.updatedAt ?? 0,
  ) || undefined;

  return (
    <Card variant="outlined" sx={{ height: '100%', bgcolor: 'background.paper' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
          <Box display="flex" alignItems="center" gap={0.75} minWidth={0} flex={1}>
            <WidgetDragHandle widgetId={widget.id} />
            <Typography variant="subtitle2" noWrap title={title} sx={{ flex: 1, minWidth: 0 }}>
              {title}
            </Typography>
          </Box>
          <IconButton size="small" aria-label={t('dashboard.remove')} onClick={onRemove}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Box display="flex" gap={2} mt={2} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={1} minWidth={120}>
            <Bolt sx={{ color: energy != null ? 'warning.main' : 'action.disabled', fontSize: 32 }} />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('dashboard.smlEnergy')}
              </Typography>
              <Typography variant="h5" fontWeight={700} color={energy != null ? 'text.primary' : 'text.secondary'}>
                {energy != null ? `${formatQty(energy, 1)} kWh` : t('common.emdash')}
              </Typography>
            </Box>
          </Box>
          {widget.topicPower ? (
            <Box display="flex" alignItems="center" gap={1} minWidth={120}>
              <Speed sx={{ color: power != null ? 'info.main' : 'action.disabled', fontSize: 32 }} />
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('dashboard.smlPower')}
                </Typography>
                <Typography variant="h5" fontWeight={700} color={power != null ? 'text.primary' : 'text.secondary'}>
                  {power != null ? `${formatQty(power, 0)} W` : t('common.emdash')}
                </Typography>
              </Box>
            </Box>
          ) : null}
          {reverse != null ? (
            <Box display="flex" alignItems="center" gap={1} minWidth={120}>
              <SwapHoriz sx={{ color: 'success.main', fontSize: 32 }} />
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('dashboard.smlReverse')}
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {`${formatQty(reverse, 1)} kWh`}
                </Typography>
              </Box>
            </Box>
          ) : null}
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          {formatAge(updatedAt, t)}
        </Typography>
      </CardContent>
    </Card>
  );
};

const DistanceWidget: React.FC<{
  widget: DashboardWidget;
  value: DashboardTopicValue | null | undefined;
  onRemove: () => void;
}> = ({ widget, value, onRemove }) => {
  const { t } = useI18n();
  const mm = readScalar(value);
  const title = widget.title || widget.topic;
  const updatedAt = value?.updatedAt;

  return (
    <Card variant="outlined" sx={{ height: '100%', bgcolor: 'background.paper' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
          <Box display="flex" alignItems="center" gap={0.75} minWidth={0} flex={1}>
            <WidgetDragHandle widgetId={widget.id} />
            <Typography variant="subtitle2" noWrap title={title} sx={{ flex: 1, minWidth: 0 }}>
              {title}
            </Typography>
          </Box>
          <IconButton size="small" aria-label={t('dashboard.remove')} onClick={onRemove}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Box display="flex" alignItems="center" gap={1} mt={2} minWidth={120}>
          <Straighten sx={{ color: mm != null ? 'success.main' : 'action.disabled', fontSize: 32 }} />
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('dashboard.distance')}
            </Typography>
            <Typography variant="h5" fontWeight={700} color={mm != null ? 'text.primary' : 'text.secondary'}>
              {mm != null ? `${Math.round(mm)} ${t('dashboard.distanceUnit')}` : t('common.emdash')}
            </Typography>
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          {formatAge(updatedAt, t)}
        </Typography>
      </CardContent>
    </Card>
  );
};

const NfcTagWidget: React.FC<{
  widget: DashboardWidget;
  values: Record<string, DashboardTopicValue | null | undefined>;
  onRemove: () => void;
}> = ({ widget, values, onRemove }) => {
  const { t } = useI18n();
  const uid = readText(values[widget.topic]);
  const presentRaw = widget.topicPresent ? readText(values[widget.topicPresent]) : '';
  const presentNum = widget.topicPresent ? readScalar(values[widget.topicPresent]) : null;
  const present = presentRaw === '1' || presentNum === 1;
  const knownPresent = Boolean(widget.topicPresent) && (presentRaw !== '' || presentNum != null);
  const title = widget.title || widget.topic;
  const updatedAt = Math.max(
    values[widget.topic]?.updatedAt ?? 0,
    values[widget.topicPresent || '']?.updatedAt ?? 0,
  ) || undefined;

  return (
    <Card variant="outlined" sx={{ height: '100%', bgcolor: 'background.paper' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
          <Box display="flex" alignItems="center" gap={0.75} minWidth={0} flex={1}>
            <WidgetDragHandle widgetId={widget.id} />
            <Typography variant="subtitle2" noWrap title={title} sx={{ flex: 1, minWidth: 0 }}>
              {title}
            </Typography>
          </Box>
          <IconButton size="small" aria-label={t('dashboard.remove')} onClick={onRemove}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Box display="flex" alignItems="center" gap={1.5} mt={2} flexWrap="wrap">
          <Nfc sx={{ color: present ? 'success.main' : 'action.disabled', fontSize: 36 }} />
          <Box minWidth={0} flex={1}>
            {knownPresent && (
              <Chip
                size="small"
                color={present ? 'success' : 'default'}
                label={present ? t('dashboard.nfcPresent') : t('dashboard.nfcAbsent')}
                sx={{ mb: 0.75 }}
              />
            )}
            <Typography variant="caption" color="text.secondary" display="block">
              {t('dashboard.nfcUid')}
            </Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
              sx={{ wordBreak: 'break-all' }}
              color={uid ? 'text.primary' : 'text.secondary'}
            >
              {uid || t('common.emdash')}
            </Typography>
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          {formatAge(updatedAt, t)}
        </Typography>
      </CardContent>
    </Card>
  );
};

const TemperatureWidget: React.FC<{
  widget: DashboardWidget;
  tempValue: DashboardTopicValue | null | undefined;
  rhValue?: DashboardTopicValue | null | undefined;
  onRemove: () => void;
}> = (props) => <ClimateWidget {...props} variant="sensor" />;

const ModbusWidget: React.FC<{
  widget: DashboardWidget;
  tempValue: DashboardTopicValue | null | undefined;
  rhValue?: DashboardTopicValue | null | undefined;
  onRemove: () => void;
}> = (props) => <ClimateWidget {...props} variant="modbus" />;

const GpsMapWidget: React.FC<{
  widget: DashboardWidget;
  values: Record<string, DashboardTopicValue | null | undefined>;
  onRemove: () => void;
}> = ({ widget, values, onRemove }) => {
  const { t } = useI18n();
  const { draggingId } = useDashboardDnd();
  const lat = readScalar(values[widget.topic]);
  const lon = readScalar(values[widget.topicLon || '']);
  const speed = widget.topicSpeed ? readScalar(values[widget.topicSpeed]) : null;
  const alt = widget.topicAlt ? readScalar(values[widget.topicAlt]) : null;
  const sat = widget.topicSat ? readScalar(values[widget.topicSat]) : null;
  const title = widget.title || widget.topic;
  const hasFix = lat != null && lon != null;
  const updatedAt = Math.max(
    values[widget.topic]?.updatedAt ?? 0,
    values[widget.topicLon || '']?.updatedAt ?? 0,
    values[widget.topicSpeed || '']?.updatedAt ?? 0,
    values[widget.topicAlt || '']?.updatedAt ?? 0,
    values[widget.topicSat || '']?.updatedAt ?? 0,
  ) || undefined;

  const mapSrc = hasFix
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(`${lon - 0.008},${lat - 0.005},${lon + 0.008},${lat + 0.005}`)}&layer=mapnik&marker=${encodeURIComponent(`${lat},${lon}`)}`
    : null;
  const mapLink = hasFix ? `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=15/${lat}/${lon}` : null;

  return (
    <Card variant="outlined" sx={{ height: '100%', bgcolor: 'background.paper', overflow: 'hidden' }}>
      <Box sx={{ position: 'relative', height: 280, bgcolor: 'grey.900' }}>
        {mapSrc ? (
          <Box
            component="iframe"
            title={title}
            src={mapSrc}
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, pointerEvents: draggingId ? 'none' : 'auto' }}
          />
        ) : (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              bgcolor: 'action.hover',
            }}
          >
            <MyLocation sx={{ fontSize: 48, color: 'action.disabled' }} />
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.gpsNoFix')}
            </Typography>
            {sat != null && (
              <Typography variant="caption" color="text.secondary">
                {Math.round(sat)} sat
              </Typography>
            )}
          </Box>
        )}

        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            right: 44,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            py: 0.5,
            borderRadius: 1,
            bgcolor: 'rgba(0,0,0,0.55)',
            color: 'common.white',
          }}
        >
          <WidgetDragHandle widgetId={widget.id} tone="onDark" />
          <MyLocation sx={{ fontSize: 18 }} />
          <Typography variant="caption" fontWeight={600} noWrap title={title}>
            {title}
          </Typography>
        </Box>

        <IconButton
          size="small"
          aria-label={t('dashboard.remove')}
          onClick={onRemove}
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            bgcolor: 'rgba(0,0,0,0.45)',
            color: 'common.white',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
          }}
        >
          <Close fontSize="small" />
        </IconButton>

        {hasFix && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              px: 1.5,
              py: 1,
              bgcolor: 'rgba(0,0,0,0.7)',
              color: 'common.white',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              fontSize: 12,
            }}
          >
            <span>{`${lat!.toFixed(5)}\u00b0, ${lon!.toFixed(5)}\u00b0`}</span>
            {speed != null && <span>{speed.toFixed(0)} km/h</span>}
            {alt != null && <span>{alt.toFixed(0)} m</span>}
            {sat != null && <span>{Math.round(sat)} sat</span>}
          </Box>
        )}
      </Box>

      <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography variant="caption" color="text.secondary">
            {formatAge(updatedAt, t)}
          </Typography>
          {mapLink && (
            <Typography
              component="a"
              href={mapLink}
              target="_blank"
              rel="noopener noreferrer"
              variant="caption"
              color="primary"
            >
              OpenStreetMap
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const SignalsWidget: React.FC<{
  widget: DashboardWidget;
  values: Record<string, DashboardTopicValue | null | undefined>;
  definition: SignalsDefinition | null | undefined;
  onRemove: () => void;
  onRefresh: () => Promise<void>;
}> = ({ widget, values, definition, onRemove, onRefresh }) => {
  const { t } = useI18n();
  const [cmdOpen, setCmdOpen] = useState(false);
  const title = widget.title || definition?.metadata?.name || widget.signalName || widget.topic;
  const target = stacklightCommandTarget(widget, definition);
  const baseTopic = target.baseTopic;
  const resolved = useMemo(
    () => resolveStacklightLive(widget, definition, values),
    [widget, definition, values],
  );
  const rows = useMemo(() => [...resolved].reverse(), [resolved]);
  const latestAt = useMemo(() => {
    let max = 0;
    for (const topic of stacklightWatchTopics(widget, definition)) {
      const at = values[topic]?.updatedAt || 0;
      if (at > max) max = at;
    }
    return max || undefined;
  }, [widget, definition, values]);

  return (
    <>
      <Card variant="outlined" sx={{ height: '100%', bgcolor: 'background.paper' }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
            <Box display="flex" alignItems="center" gap={1} minWidth={0}>
              <WidgetDragHandle widgetId={widget.id} />
              <SignalsIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2" noWrap title={title}>
                {title}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.25}>
              <Button size="small" onClick={() => setCmdOpen(true)}>
                {t('dashboard.signalsCommands')}
              </Button>
              <IconButton size="small" aria-label={t('dashboard.remove')} onClick={onRemove}>
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Box display="flex" gap={2} mt={1.5} mb={1} alignItems="flex-start">
            <Box
              role="button"
              tabIndex={0}
              aria-label={t('dashboard.signalsCommands')}
              title={t('dashboard.signalsCommands')}
              onClick={() => setCmdOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setCmdOpen(true);
                }
              }}
              sx={{
                cursor: 'pointer',
                borderRadius: 1,
                flexShrink: 0,
                '&:hover': { filter: 'brightness(1.08)' },
                '&:focus-visible': {
                  outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: 2,
                },
              }}
            >
              <SignalsPreview
                definition={definition || undefined}
                signals={definition?.signals}
                resolvedStatuses={resolved}
                size="compact"
                showLabels={false}
              />
            </Box>
            <Box flex={1} minWidth={0}>
              {rows.length === 0 ? (
                <Typography variant="caption" color="text.secondary">{t('dashboard.noData')}</Typography>
              ) : (
                rows.map((row) => (
                  <Box key={row.slotId} display="flex" alignItems="center" gap={0.75} mb={0.5}>
                    <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: row.color, flexShrink: 0 }} />
                    <Typography variant="caption" noWrap title={`${row.slotName}: ${row.name}`}>
                      <Box component="span" color="text.secondary">{row.slotName}: </Box>
                      {row.name}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </Box>

          <Typography variant="caption" color="text.secondary" display="block" noWrap title={baseTopic}>
            {baseTopic ? `${baseTopic}/{Signal}` : t('dashboard.signalsNoTopic')}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {formatAge(latestAt, t)}
          </Typography>
        </CardContent>
      </Card>

      <SignalsCommandDialog
        open={cmdOpen}
        onClose={() => setCmdOpen(false)}
        title={title}
        definition={definition}
        {...target}
        values={values}
        onPublished={onRefresh}
      />
    </>
  );
};

function readSwitchOn(value: DashboardTopicValue | null | undefined): boolean | null {
  if (!value) {
    return null;
  }
  if (typeof value.value === 'number' && Number.isFinite(value.value)) {
    return value.value !== 0;
  }
  const raw = readText(value).trim().toLowerCase();
  if (!raw) {
    return null;
  }
  if (raw === '1' || raw === 'on' || raw === 'true' || raw === 'ein' || raw === 'yes') {
    return true;
  }
  if (raw === '0' || raw === 'off' || raw === 'false' || raw === 'aus' || raw === 'no') {
    return false;
  }
  const n = Number(raw);
  if (Number.isFinite(n)) {
    return n !== 0;
  }
  return null;
}

const SwitchWidget: React.FC<{
  widget: DashboardWidget;
  value: DashboardTopicValue | null | undefined;
  onRemove: () => void;
  onRefresh: () => void;
}> = ({ widget, value, onRemove, onRefresh }) => {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [optimistic, setOptimistic] = useState<boolean | null>(null);
  const live = readSwitchOn(value);
  const on = optimistic != null ? optimistic : (live === true);
  const title = widget.title || widget.topic;
  const bulb = widget.icon === 'bulb';

  useEffect(() => {
    if (live != null) {
      setOptimistic(null);
    }
  }, [live]);

  const toggle = async () => {
    if (!widget.topic || busy) {
      return;
    }
    const next = !on;
    setOptimistic(next);
    setBusy(true);
    try {
      await dashboardApi.publishMqtt(widget.topic, next ? '1' : '0', { retain: true });
      onRefresh();
    } catch (err) {
      setOptimistic(null);
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ height: '100%', bgcolor: 'background.paper' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
          <Box display="flex" alignItems="center" gap={0.75} minWidth={0} flex={1}>
            <WidgetDragHandle widgetId={widget.id} />
            <Box minWidth={0} flex={1}>
              <Typography variant="subtitle2" noWrap title={title}>
                {title}
              </Typography>
              {widget.topic ? (
                <Typography variant="caption" color="text.secondary" noWrap display="block" title={widget.topic}>
                  {widget.topic}
                </Typography>
              ) : null}
            </Box>
          </Box>
          <IconButton size="small" onClick={onRemove} aria-label={t('dashboard.remove')}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Box display="flex" alignItems="center" justifyContent="center" gap={1.5} mt={2} mb={1}>
          {bulb ? (
            <Lightbulb
              sx={{ fontSize: 48, opacity: on ? 1 : 0.35, color: on ? 'warning.main' : 'action.disabled' }}
              aria-label={on ? t('dashboard.switchOn') : t('dashboard.switchOff')}
            />
          ) : (
            <>
              <ToggleOn sx={{ fontSize: 28, color: on ? 'success.main' : 'action.disabled' }} />
              <Switch
                checked={on}
                onChange={() => { void toggle(); }}
                disabled={busy || !widget.topic}
                color="success"
                inputProps={{ 'aria-label': title }}
              />
            </>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" textAlign="center">
          {live == null && optimistic == null
            ? t('dashboard.noData')
            : (on ? t('dashboard.switchOn') : t('dashboard.switchOff'))}
          {' · '}
          {formatAge(value?.updatedAt, t)}
        </Typography>
      </CardContent>
    </Card>
  );
};

const RelayWidget: React.FC<{
  widget: DashboardWidget;
  values: Record<string, DashboardTopicValue | null | undefined>;
  onRemove: () => void;
  onRefresh: () => void;
}> = ({ widget, values, onRemove, onRefresh }) => {
  const { t } = useI18n();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [optimistic, setOptimistic] = useState<Record<string, boolean>>({});
  const channels = Array.isArray(widget.channels) ? widget.channels : [];
  const title = widget.title || widget.hostname || t('flow.relayWidget');
  const latestAt = useMemo(() => {
    let max = 0;
    for (const ch of channels) {
      const at = ch.topic ? values[ch.topic]?.updatedAt || 0 : 0;
      if (at > max) max = at;
    }
    return max || undefined;
  }, [channels, values]);

  const toggle = async (ch: NonNullable<DashboardWidget['channels']>[number]) => {
    const id = `${ch.of}.${ch.key}`;
    if (busyKey) {
      return;
    }
    const live = ch.topic ? readSwitchOn(values[ch.topic]) : null;
    const current = optimistic[id] != null ? optimistic[id] : live === true;
    const next = !current;
    setOptimistic((prev) => ({ ...prev, [id]: next }));
    setBusyKey(id);
    try {
      await dashboardApi.commandRelay({
        hostname: ch.hostname || widget.hostname,
        of: ch.of,
        key: ch.key,
        on: next,
      });
      onRefresh();
    } catch (err) {
      setOptimistic((prev) => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
      console.error(err);
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Card variant="outlined" sx={{ height: '100%', bgcolor: 'background.paper' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
          <Box display="flex" alignItems="center" gap={0.75} minWidth={0} flex={1}>
            <WidgetDragHandle widgetId={widget.id} />
            <Typography variant="subtitle2" noWrap title={title}>
              {title}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onRemove} aria-label={t('dashboard.remove')}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Box mt={1}>
          {channels.length === 0 ? (
            <Typography variant="caption" color="text.secondary">{t('dashboard.noData')}</Typography>
          ) : channels.map((ch) => {
            const id = `${ch.of}.${ch.key}`;
            const live = ch.topic ? readSwitchOn(values[ch.topic]) : null;
            const on = optimistic[id] != null ? optimistic[id] : live === true;
            const writable = ch.writable !== false;
            return (
              <Box key={`${ch.channel}:${id}`} display="flex" alignItems="center" justifyContent="space-between" gap={1} py={0.25}>
                <Typography variant="body2" noWrap title={ch.name}>
                  {ch.name || `${ch.of}.${ch.key}`}
                </Typography>
                {writable ? (
                  <Switch
                    size="small"
                    checked={on}
                    onChange={() => { void toggle(ch); }}
                    disabled={busyKey != null}
                    color="success"
                    inputProps={{ 'aria-label': ch.name }}
                  />
                ) : (
                  <Lightbulb
                    fontSize="small"
                    sx={{ color: on ? 'warning.main' : 'action.disabled', opacity: on ? 1 : 0.45 }}
                    aria-label={ch.name}
                  />
                )}
              </Box>
            );
          })}
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          {formatAge(latestAt, t)}
        </Typography>
      </CardContent>
    </Card>
  );
};

const DashboardPage: React.FC = () => {
  const { t } = useI18n();
  const [widgets, setWidgets] = useState<DashboardWidget[]>([]);
  const [signalDefs, setSignalDefs] = useState<Record<string, SignalsDefinition>>({});
  const [catalog, setCatalog] = useState<WidgetCatalogItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const widgetTopics = useMemo(() => {
    const topics = new Set<string>();
    for (const w of widgets) {
      if (w.type === 'stacklight') {
        const def = w.signalName ? signalDefs[w.signalName] : undefined;
        stacklightWatchTopics(w, def).forEach((topic) => topics.add(topic));
      } else if (w.type === 'relay') {
        for (const ch of w.channels || []) {
          if (ch.topic) topics.add(ch.topic);
        }
      } else if (w.topic) {
        topics.add(w.topic);
        if (w.topicRh) topics.add(w.topicRh);
        if (w.topicLon) topics.add(w.topicLon);
        if (w.topicSpeed) topics.add(w.topicSpeed);
        if (w.topicAlt) topics.add(w.topicAlt);
        if (w.topicSat) topics.add(w.topicSat);
        if (w.topicPower) topics.add(w.topicPower);
        if (w.topicReverse) topics.add(w.topicReverse);
        if (w.topicPresent) topics.add(w.topicPresent);
      }
    }
    return Array.from(topics);
  }, [widgets, signalDefs]);
  const { values, mqttConfigured, refresh: refreshValues } = useLiveMqttValues(widgetTopics);

  const loadSignalDefs = useCallback(async (list: DashboardWidget[]) => {
    const names = Array.from(new Set(
      list
        .filter((w) => w.type === 'stacklight' && w.signalName)
        .map((w) => w.signalName!),
    ));
    if (names.length === 0) return;
    const next: Record<string, SignalsDefinition> = {};
    await Promise.all(names.map(async (name) => {
      try {
        next[name] = await signalsApi.get(name);
      } catch {
        // ignore
      }
    }));
    setSignalDefs((prev) => ({ ...prev, ...next }));
  }, []);

  const persist = useCallback(async (next: DashboardWidget[], opts?: { refreshValues?: boolean }) => {
    setSaving(true);
    try {
      setWidgets(next);
      await dashboardApi.saveDashboard({ widgets: next });
      setDirty(false);
      await loadSignalDefs(next);
      if (opts?.refreshValues !== false) {
        await refreshValues();
      }
    } finally {
      setSaving(false);
    }
  }, [loadSignalDefs, refreshValues]);

  const markLocal = useCallback((next: DashboardWidget[]) => {
    setWidgets(next);
    setDirty(true);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError(null);
        const [dash, liveCatalog] = await Promise.all([
          dashboardApi.getDashboard(),
          dashboardApi.getCatalog().catch(() => [] as WidgetCatalogItem[]),
        ]);
        const incoming = dash.widgets || [];
        const next = incoming
          .filter(isKnownDashboardWidget)
          .map((w) => (w.type === 'stacklight' ? bindStacklightFromCatalog(w, liveCatalog) : w));
        if (cancelled) return;
        setWidgets(next);
        setDirty(false);
        await loadSignalDefs(next);
      } catch (err) {
        if (!cancelled) {
          setError(t('dashboard.loadError'));
          console.error(err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadSignalDefs, t]);

  const openAdd = async () => {
    try {
      const items = await dashboardApi.getCatalog();
      setCatalog(items);
      setAddOpen(true);
    } catch (err) {
      setError(t('dashboard.loadError'));
      console.error(err);
    }
  };

  const widgetKey = (item: {
    type: string; id?: string; catalogId?: string;
    topic?: string; signalName?: string; stacklight?: string; hostname?: string;
  }) => (
    item.type === 'stacklight'
      ? `stacklight:${String(item.hostname || '').toLowerCase()}:${item.stacklight || item.signalName || ''}`
      : item.type === 'relay'
        ? `relay:${item.catalogId || item.id || ''}`
        : `${item.type}:${item.topic || ''}:${item.signalName || ''}`
  );

  const placedKeys = useMemo(
    () => new Set(widgets.map(widgetKey)),
    [widgets]
  );

  const available = catalog.filter((c) => !c.relayChannel && !placedKeys.has(widgetKey(c)));

  const addWidget = async (item: WidgetCatalogItem) => {
    const next: DashboardWidget[] = [
      ...widgets,
      {
        id: `w_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        type: item.type,
        deviceId: item.deviceId,
        topic: item.topic,
        title: item.label || item.hostname,
        signalName: item.signalName,
        stacklight: item.stacklight,
        hostname: item.hostname,
        topicRh: item.topicRh,
        topicLon: item.topicLon,
        topicSpeed: item.topicSpeed,
        topicAlt: item.topicAlt,
        topicSat: item.topicSat,
        topicPower: item.topicPower,
        topicReverse: item.topicReverse,
        topicPresent: item.topicPresent,
        icon: item.icon,
        flowName: item.flowName,
        catalogId: item.catalogId || item.id,
        channels: item.channels,
      },
    ];
    setAddOpen(false);
    markLocal(next);
    await loadSignalDefs(next);
  };

  const removeWidget = (id: string) => {
    markLocal(widgets.filter((w) => w.id !== id));
  };

  const moveWidget = (fromId: string, toId: string) => {
    const next = reorderWidgets(widgets, fromId, toId);
    if (next === widgets) {
      return;
    }
    markLocal(next);
  };

  const saveDashboard = async () => {
    try {
      setError(null);
      await persist(widgets);
    } catch (err) {
      setError(t('dashboard.saveError'));
      console.error(err);
    }
  };

  const dndValue = useMemo(
    () => ({ enabled: widgets.length > 1, draggingId, setDraggingId, setOverId }),
    [widgets.length, draggingId],
  );

  return (
    <Container maxWidth={false} sx={{ mt: 3, mb: 4, px: { xs: 2, md: 3 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
        <Box>
          <Typography variant="h4" component="h1">
            {t('dashboard.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('dashboard.subtitle')}
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<Save />}
            onClick={saveDashboard}
            disabled={!dirty || saving}
          >
            {t('dashboard.save')}
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={openAdd}>
            {t('dashboard.addWidget')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!mqttConfigured && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('dashboard.mqttMissing')}
        </Alert>
      )}

      {widgets.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary">{t('dashboard.empty')}</Typography>
            <Button component={RouterLink} to={SETUP_FLOW_PATH} sx={{ mt: 2 }}>
              {t('nav.flow')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <DashboardDndContext.Provider value={dndValue}>
        <Grid container spacing={2}>
          {widgets.map((widget) => (
            <Grid
              item
              xs={12}
              sm={widget.type === 'gps_map' ? 12 : 6}
              md={widget.type === 'gps_map' ? 6 : 4}
              lg={widget.type === 'gps_map' ? 5 : 3}
              key={widget.id}
            >
              <DashboardWidgetSlot
                widgetId={widget.id}
                draggingId={draggingId}
                overId={overId}
                setOverId={setOverId}
                onDropOn={moveWidget}
              >
              {widget.type === 'temperature' ? (
                <TemperatureWidget
                  widget={widget}
                  tempValue={values[widget.topic]}
                  rhValue={widget.topicRh ? values[widget.topicRh] : undefined}
                  onRemove={() => removeWidget(widget.id)}
                />
              ) : widget.type === 'modbus' ? (
                <ModbusWidget
                  widget={widget}
                  tempValue={values[widget.topic]}
                  rhValue={widget.topicRh ? values[widget.topicRh] : undefined}
                  onRemove={() => removeWidget(widget.id)}
                />
              ) : widget.type === 'gps_map' ? (
                <GpsMapWidget
                  widget={widget}
                  values={values}
                  onRemove={() => removeWidget(widget.id)}
                />
              ) : widget.type === 'sml_meter' ? (
                <SmlMeterWidget
                  widget={widget}
                  values={values}
                  onRemove={() => removeWidget(widget.id)}
                />
              ) : widget.type === 'nfc_tag' ? (
                <NfcTagWidget
                  widget={widget}
                  values={values}
                  onRemove={() => removeWidget(widget.id)}
                />
              ) : widget.type === 'distance' ? (
                <DistanceWidget
                  widget={widget}
                  value={values[widget.topic]}
                  onRemove={() => removeWidget(widget.id)}
                />
              ) : widget.type === 'stacklight' ? (
                <SignalsWidget
                  widget={widget}
                  values={values}
                  definition={widget.signalName
                    ? signalDefs[widget.signalName]
                    : null}
                  onRemove={() => removeWidget(widget.id)}
                  onRefresh={refreshValues}
                />
              ) : widget.type === 'switch' ? (
                <SwitchWidget
                  widget={widget}
                  value={values[widget.topic]}
                  onRemove={() => removeWidget(widget.id)}
                  onRefresh={refreshValues}
                />
              ) : widget.type === 'relay' ? (
                <RelayWidget
                  widget={widget}
                  values={values}
                  onRemove={() => removeWidget(widget.id)}
                  onRefresh={refreshValues}
                />
              ) : (
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <WidgetDragHandle widgetId={widget.id} />
                      <Typography>{widget.type}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              )}
              </DashboardWidgetSlot>
            </Grid>
          ))}
        </Grid>
        </DashboardDndContext.Provider>
      )}

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('dashboard.addWidget')}</DialogTitle>
        <DialogContent dividers>
          {available.length === 0 ? (
            <Box>
              <Typography color="text.secondary">{t('dashboard.catalogHint')}</Typography>
              <Button component={RouterLink} to={SETUP_FLOW_PATH} sx={{ mt: 1.5 }} onClick={() => setAddOpen(false)}>
                {t('nav.flow')}
              </Button>
            </Box>
          ) : (
            <List disablePadding>
              {available.map((item) => (
                <ListItemButton key={item.id} onClick={() => addWidget(item)}>
                  <ListItemText
                    primary={item.label || item.hostname}
                    secondary={[
                      item.flowName ? t('dashboard.fromFlow', { name: item.flowName }) : '',
                      item.type,
                      item.topic || item.signalName || '',
                    ].filter(Boolean).join(' \u00b7 ')}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>{t('common.cancel')}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DashboardPage;

